/* ============================================================
   ÉPRIS Wishlist UI
   ============================================================
   Verantwortlich für:
   - Click-Handling auf .epris-heart Buttons (Card + Pill + Header)
   - Sync UI-State (aria-pressed, label, count) mit window.eprisWishlist
   - Particle-Animation bei Add (radial burst, ~6 Partikel)
   - Bouncy Pop-Animation auf Heart-Icon
   - Header-Counter live updaten

   CSS wird in theme.liquid <style> block geladen (im <head>) damit
   kein FOUC auf den Heart-Icons beim initial render passiert.

   Defensive: wartet auf window.eprisWishlist (Core), läuft sonst no-op.
   ============================================================ */

(function() {
  'use strict';

  function ready() {
    if (!window.eprisWishlist) {
      /* Core nicht geladen → 100ms später retry, max 20× */
      if ((ready._retries = (ready._retries || 0) + 1) > 20) return;
      return setTimeout(ready, 100);
    }
    init();
  }

  function init() {
    /* 1. Initial-State auf alle Hearts anwenden */
    syncAllHearts();
    syncCounter();
    refreshSnapshotsFromPage();

    /* 2. Subscribe auf Core-Changes → re-sync UI */
    window.eprisWishlist.onChange(() => {
      syncAllHearts();
      syncCounter();
    });

    /* 3. Global Click-Handler — capture phase, damit wir VOR
       parent-link-navigation feuern (Heart in Card sitzt in <a>-Tag) */
    document.addEventListener('click', onHeartClick, true);

    /* 4. Re-sync wenn neue Hearts dynamisch ins DOM kommen
       (z.B. Quick-Buy-Modal, Pagination, AJAX-load).
       Debounced (200ms) um Loops mit Drawer-render zu verhindern und
       um nicht bei jedem particle-spawn neu zu syncen. */
    let moTimer;
    const mo = new MutationObserver(() => {
      clearTimeout(moTimer);
      moTimer = setTimeout(() => {
        syncAllHearts();
        refreshSnapshotsFromPage();
      }, 200);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* Refresh snapshot-data: für jedes wishlist-item das einen Heart auf
     der Page hat, update wir die snapshot mit aktuellen Daten (Title,
     Image, Price). Hält gespeicherte Daten frisch + füllt ID-only-Items
     vom Customer-Metafield-Sync auf. */
  function refreshSnapshotsFromPage() {
    if (!window.eprisWishlist) return;
    const wishedIds = new Set(window.eprisWishlist.getIds());
    if (wishedIds.size === 0) return;
    document.querySelectorAll('.epris-heart[data-variant-id][data-product-handle]').forEach(heart => {
      const vid = heart.dataset.variantId;
      if (!wishedIds.has(vid)) return;
      const snapshot = {
        handle: heart.dataset.productHandle,
        title: heart.dataset.productTitle,
        image: heart.dataset.productImage,
        price: heart.dataset.productPrice,
        compare_at_price: heart.dataset.productCompare || null,
        url: heart.dataset.productUrl,
        vendor: heart.dataset.productVendor || '',
        available: heart.dataset.productAvailable !== 'false'
      };
      /* Nur überschreiben wenn die Page-Daten sinnvoll sind */
      if (snapshot.title && snapshot.url) {
        window.eprisWishlist.updateSnapshot(vid, snapshot);
      }
    });
  }

  /* ---------- Sync UI to State ---------- */
  function syncAllHearts() {
    const hearts = document.querySelectorAll('.epris-heart[data-variant-id]');
    hearts.forEach(syncHeart);
  }

  function syncHeart(heart) {
    const vid = heart.dataset.variantId;
    if (!vid) return;
    const isActive = window.eprisWishlist.has(vid);
    const wasActive = heart.getAttribute('aria-pressed') === 'true';
    if (isActive === wasActive) return; /* nichts zu tun */

    heart.setAttribute('aria-pressed', String(isActive));

    /* Pill-Label switch */
    const label = heart.querySelector('.epris-heart__label');
    if (label) {
      const labelAdd = label.dataset.labelAdd;
      const labelRemove = label.dataset.labelRemove;
      if (labelAdd && labelRemove) {
        label.textContent = isActive ? labelRemove : labelAdd;
      }
    }
  }

  function syncCounter() {
    const count = window.eprisWishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = String(count);
      if (count > 0) {
        el.classList.add('is-visible');
      } else {
        el.classList.remove('is-visible');
      }
    });
    /* Header-Heart füllt sich sobald mind. 1 Item in Wishlist
       (eigene .has-items Klasse statt aria-pressed — semantisch korrekt
       weil Header-Heart Opener ist, kein Toggle).
       Pop-Animation NUR bei transition 0→1+ (nicht auf jedem Page-Load). */
    document.querySelectorAll('.epris-heart--header').forEach(heart => {
      const wasEmpty = !heart.classList.contains('has-items');
      heart.classList.toggle('has-items', count > 0);
      if (wasEmpty && count > 0) {
        heart.classList.remove('is-just-filled');
        void heart.offsetHeight; /* reflow */
        heart.classList.add('is-just-filled');
        setTimeout(() => heart.classList.remove('is-just-filled'), 500);
      }
    });
  }

  /* ---------- Click Handler ---------- */
  function onHeartClick(e) {
    const heart = e.target.closest('.epris-heart');
    if (!heart) return;

    /* Wir intercepten den Click, damit parent-<a>-Navigation nicht feuert */
    e.preventDefault();
    e.stopPropagation();

    /* Header-Heart: kein Toggle — öffnet nur den Drawer */
    if (heart.classList.contains('epris-heart--header')) {
      document.dispatchEvent(new CustomEvent('epris:wishlist:open-drawer'));
      return;
    }

    const vid = heart.dataset.variantId;
    if (!vid) return;

    const willBeActive = !window.eprisWishlist.has(vid);

    /* Animation FIRST (visuell snappy), dann state-toggle */
    if (willBeActive) {
      animatePop(heart);
      spawnParticles(heart);
    } else {
      /* Subtle shrink-feedback beim Remove */
      animateShrink(heart);
    }

    /* Snapshot aus dataset bauen (für Drawer-Render ohne extra fetch) */
    const snapshot = {
      handle: heart.dataset.productHandle,
      title: heart.dataset.productTitle,
      image: heart.dataset.productImage,
      price: heart.dataset.productPrice,
      compare_at_price: heart.dataset.productCompare || null,
      url: heart.dataset.productUrl,
      vendor: heart.dataset.productVendor || '',
      available: heart.dataset.productAvailable !== 'false'
    };

    /* State toggle mit snapshot — triggert via onChange auch syncHeart() */
    window.eprisWishlist.toggle(vid, snapshot);
  }

  /* ---------- Animations ---------- */
  function animatePop(heart) {
    heart.classList.remove('is-popping', 'is-shrinking');
    /* Force reflow um animation re-trigger zu erzwingen */
    void heart.offsetHeight;
    heart.classList.add('is-popping');
    setTimeout(() => heart.classList.remove('is-popping'), 600);
  }

  function animateShrink(heart) {
    heart.classList.remove('is-popping', 'is-shrinking');
    void heart.offsetHeight;
    heart.classList.add('is-shrinking');
    setTimeout(() => heart.classList.remove('is-shrinking'), 300);
  }

  function spawnParticles(heart) {
    const container = heart.querySelector('.epris-heart__particles');
    if (!container) return;

    const count = 6;
    const baseRadius = heart.classList.contains('epris-heart--pill') ? 22 : 18;

    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 / count) * i + (Math.random() * 0.4 - 0.2);
      const distance = baseRadius + Math.random() * 6;
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance;

      const p = document.createElement('span');
      p.className = 'epris-heart-particle';
      p.style.setProperty('--dx', dx.toFixed(1) + 'px');
      p.style.setProperty('--dy', dy.toFixed(1) + 'px');
      container.appendChild(p);

      /* Auto-cleanup nach Animation-Ende (animation-duration ist 0.7s) */
      setTimeout(() => { p.remove(); }, 800);
    }
  }

  /* ---------- Boot ---------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }
})();
